import { animate, style, transition, trigger } from '@angular/animations';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  OnInit,
  Input,
  ElementRef,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
  afterNextRender,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CardLayout } from '@modules/card/enums/card-layout.enum';
import { MediaImageModel } from '@modules/media/models/media-image.model';
import { Screen, WindowService } from '@modules/shared/services/window.service';
import {
  ActionIcons,
  ButtonItem,
  ActionContainerComponent,
} from '@modules/ui/action-container/action-container.component';

import { MediaComponent } from '../../media/media.component';

interface GallerySetInterface {
  rowTotalRatio: number;
  items: GallerySetItemInterface[];
}
interface GallerySetItemInterface {
  flexBasis: number;
  galleryItem: MediaWithIndex;
  imgSrc: string;
}

interface MediaWithIndex {
  index: number;
  media: MediaImageModel;
}

// Used to pick an image asset before the real container width is known (SSR + pre-hydration client render).
const FALLBACK_IMAGE_WIDTH = 800;

@Component({
  selector: 'mb-article-gallery',
  templateUrl: './article-gallery.component.html',
  styleUrls: ['./article-gallery.component.scss'],
  animations: [trigger('fadeIn', [transition(':enter', [style({ opacity: 0 }), animate('400ms 100ms ease-out')])])],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgTemplateOutlet, MediaComponent, ActionContainerComponent, TranslatePipe],
})
export class ArticleGalleryComponent implements OnInit, OnDestroy {
  private el = inject(ElementRef);
  private ws = inject(WindowService);
  private cdr = inject(ChangeDetectorRef);

  public cardLayout = CardLayout;
  public initiallyVisibleRows = 3;
  public displayedRows = 3;
  public moreRows = 3;
  public setWrapperClass = ['set-wrapper'];
  public itemWrapperClass = ['item-wrapper'];

  @Input() buttonItems: ButtonItem[] = [];

  private minHeights = {
    [Screen.XXXS]: 140,
    [Screen.XXS]: 140,
    [Screen.XS]: 200,
    [Screen.SM]: 200,
    [Screen.MD]: 200,
    [Screen.LG]: 280,
    [Screen.XL]: 280,
    [Screen.XXL]: 340,
  };
  private destroy$ = new Subject<boolean>();
  // Stays false on the server and during the pre-hydration client render so both produce the
  // same width-independent DOM structure; flips true once afterNextRender confirms hydration finished.
  private hydrated = false;

  public sets: GallerySetInterface[] = [];
  private _items!: MediaImageModel[];
  @Input()
  set items(v: MediaImageModel[]) {
    this._items = v;
    this.buildItemSet(this.ws.screen);
  }
  get items() {
    return this._items;
  }

  @Output() itemClicked = new EventEmitter<MediaWithIndex>();
  @Output() itemActionClicked = new EventEmitter<{ action: ActionIcons; media: MediaImageModel }>();

  constructor() {
    afterNextRender(() => {
      this.hydrated = true;
      this.buildItemSet(this.ws.screen);
      this.cdr.detectChanges();
    });
  }

  ngOnInit() {
    this.ws.screen$.pipe(takeUntil(this.destroy$)).subscribe((screen) => {
      this.buildItemSet(screen);
      this.cdr.detectChanges();
    });
  }

  private buildItemSet(screen: Screen) {
    if (!this.hydrated) {
      this.buildFallbackItemSet();
      return;
    }
    const galleryWidth = this.el.nativeElement.clientWidth;
    const minHeight = this.minHeights[screen];
    const calcRatio = galleryWidth / minHeight;
    let currentRatio = 0;
    this.sets = [];
    let setRow: MediaWithIndex[] = [];
    let currentSetBasis = 0;
    this.items.forEach((item, index) => {
      const currentBasis = (item.ratio / calcRatio) * 100;
      if (currentSetBasis + currentBasis >= 100) {
        this.sets.push({
          rowTotalRatio: currentRatio,
          items: setRow.map((item) => this.getSetItem(item, currentRatio)),
        });

        currentSetBasis = 0;
        currentRatio = 0;
        setRow = [];
      }
      setRow.push({
        index,
        media: item,
      });
      currentSetBasis += currentBasis || 0;
      currentRatio += item.ratio;
    });
    if (setRow.length) {
      if (this.sets.length && currentRatio <= calcRatio / 2) {
        const last = this.sets.pop() as GallerySetInterface;
        last.rowTotalRatio = last.rowTotalRatio + currentRatio;
        last.items = last.items.map((item) => this.getSetItem(item.galleryItem, last.rowTotalRatio));
        last.items = last.items.concat(setRow.map((item) => this.getSetItem(item, last.rowTotalRatio)));
        this.sets.push(last);
      } else {
        this.sets.push({
          rowTotalRatio: currentRatio,
          items: setRow.map((item) => this.getSetItem(item, currentRatio)),
        });
      }
    }

    if (this.sets.length === 1 && this.sets[0].items.length === 1) {
      this.addSingleItemClasses();
    }
  }

  // One full-width row per image: deterministic and independent of container width, so it renders
  // identically on the server and on the client before hydration completes.
  private buildFallbackItemSet(): void {
    this.sets = this.items.map((media, index) => ({
      rowTotalRatio: media.ratio,
      items: [
        {
          flexBasis: 100,
          imgSrc: media.closestToWidth(FALLBACK_IMAGE_WIDTH).url,
          galleryItem: { index, media },
        },
      ],
    }));

    if (this.sets.length === 1) {
      this.addSingleItemClasses();
    }
  }

  private getSetItem(galleryItem: MediaWithIndex, currentRatio: number): GallerySetItemInterface {
    const galleryWidth = this.el.nativeElement.clientWidth;
    const basis = galleryItem.media.ratio / currentRatio;
    const minWidth = Math.ceil((galleryWidth * basis) / 100) * 100;
    return {
      flexBasis: basis * 100,
      imgSrc: galleryItem.media.closestToWidth(minWidth).url,
      galleryItem: galleryItem,
    };
  }

  private addSingleItemClasses(): void {
    this.setWrapperClass.push('single-set-wrapper');
    this.itemWrapperClass.push('single-item-wrapper');
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
